import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  bootstrapTrafficSegments,
  createTrafficSegment,
  deleteTrafficSegment,
  deleteTrafficSourceRule,
  discoverTrafficSources,
  getApiErrorMessage,
  getTrafficSegmentation,
  syncTrafficAttributions,
  updateTrafficSegment,
  updateTrafficSegmentation,
  upsertTrafficSourceRule,
} from '../api'
import { EmptyState, LoadingBlock, Section, StatusPill } from './v2/V2Primitives'

const INTENSITY_OPTIONS = [
  ['inherit', 'Inherit base graph'],
  ['relationship_first', 'Relationship-first'],
  ['soft', 'Soft'],
  ['balanced', 'Balanced'],
  ['direct', 'Direct'],
  ['aggressive', 'Aggressive'],
]

const EMPTY_SEGMENT = {
  key: '',
  name: '',
  description: '',
  is_active: true,
  is_default: false,
  intensity: 'balanced',
  target_first_offer_turn: 8,
  free_presell_turns: 1,
  offer_cooldown_messages: 6,
  proactive_presell: true,
  relationship_first: false,
  strategy_notes: '',
  overlay_label: '',
  overlay_prompt: '',
  overlay_facts: '',
  forbidden_claims: '',
}

function segmentDraft(segment) {
  if (!segment) return { ...EMPTY_SEGMENT }
  const policy = segment.sales_policy || {}
  const overlay = segment.persona_overlay || {}
  return {
    key: segment.key || '',
    name: segment.name || '',
    description: segment.description || '',
    is_active: segment.is_active !== false,
    is_default: Boolean(segment.is_default),
    intensity: policy.intensity || 'balanced',
    target_first_offer_turn: Number(policy.target_first_offer_turn || 8),
    free_presell_turns: Number(policy.free_presell_turns || 0),
    offer_cooldown_messages: Number(policy.offer_cooldown_messages || 0),
    proactive_presell: Boolean(policy.proactive_presell),
    relationship_first: Boolean(policy.relationship_first),
    strategy_notes: policy.strategy_notes || '',
    overlay_label: overlay.label || '',
    overlay_prompt: overlay.prompt || '',
    overlay_facts: (overlay.facts || []).join('\n'),
    forbidden_claims: (overlay.forbidden_claims || []).join('\n'),
  }
}

function lines(value) {
  return String(value || '')
    .split('\n')
    .map((item) => item.trim())
    .filter(Boolean)
}

function segmentPayload(draft) {
  return {
    key: String(draft.key || '').trim(),
    name: String(draft.name || '').trim(),
    description: String(draft.description || '').trim() || null,
    is_active: Boolean(draft.is_active),
    is_default: Boolean(draft.is_default),
    persona_overlay: {
      label: String(draft.overlay_label || '').trim(),
      prompt: String(draft.overlay_prompt || '').trim(),
      facts: lines(draft.overlay_facts),
      forbidden_claims: lines(draft.forbidden_claims),
    },
    sales_policy: {
      intensity: draft.intensity,
      target_first_offer_turn: Number(draft.target_first_offer_turn || 1),
      free_presell_turns: Number(draft.free_presell_turns || 0),
      offer_cooldown_messages: Number(draft.offer_cooldown_messages || 0),
      proactive_presell: Boolean(draft.proactive_presell),
      relationship_first: Boolean(draft.relationship_first),
      strategy_notes: String(draft.strategy_notes || '').trim(),
    },
  }
}

function sourceTitle(source) {
  return source.campaign_name || source.campaign_code || `Tracking link ${source.tracking_link_id}`
}

export default function TrafficSegmentationPanel({ modelId }) {
  const [data, setData] = useState(null)
  const [sources, setSources] = useState([])
  const [sourceSegments, setSourceSegments] = useState({})
  const [sourcePriorities, setSourcePriorities] = useState({})
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState('')
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')
  const [editingId, setEditingId] = useState(null)
  const [draft, setDraft] = useState({ ...EMPTY_SEGMENT })

  const load = useCallback(async () => {
    const result = await getTrafficSegmentation(modelId)
    setData(result)
    return result
  }, [modelId])

  useEffect(() => {
    let alive = true
    setLoading(true)
    load()
      .catch((err) => {
        if (alive) setError(getApiErrorMessage(err, 'Failed to load traffic segmentation'))
      })
      .finally(() => {
        if (alive) setLoading(false)
      })
    return () => { alive = false }
  }, [load])

  const segmentsById = useMemo(() => Object.fromEntries(
    (data?.segments || []).map((segment) => [String(segment.id), segment]),
  ), [data?.segments])

  async function run(key, work, success) {
    setBusy(key)
    setError('')
    setMessage('')
    try {
      const result = await work()
      if (success) setMessage(typeof success === 'function' ? success(result) : success)
      return result
    } catch (err) {
      setError(getApiErrorMessage(err, 'Request failed'))
      return null
    } finally {
      setBusy('')
    }
  }

  async function bootstrap() {
    const result = await run(
      'bootstrap',
      () => bootstrapTrafficSegments(modelId),
      (value) => `${value?.created || 0} preset segments created`,
    )
    if (result) await load()
  }

  async function toggleEnabled() {
    const next = !data.enabled
    const result = await run(
      'toggle',
      () => updateTrafficSegmentation(modelId, next),
      next ? 'Traffic-source policy enabled' : 'Traffic-source policy disabled',
    )
    if (result) setData((current) => ({ ...current, enabled: Boolean(result.enabled) }))
  }

  async function discover() {
    const result = await run(
      'discover',
      () => discoverTrafficSources(modelId),
      (value) => `${value?.sources?.length || 0} tracking links found`,
    )
    if (!result) return
    const found = result.sources || []
    setSources(found)
    setSourceSegments(Object.fromEntries(found.map((source) => [
      `${source.provider_link_id}:${source.tracking_link_id}`,
      source.segment_id ? String(source.segment_id) : '',
    ])))
    setSourcePriorities(Object.fromEntries(found.map((source) => [
      `${source.provider_link_id}:${source.tracking_link_id}`,
      Number(source.rule_priority || 0),
    ])))
  }

  async function syncNow() {
    const result = await run(
      'sync',
      () => syncTrafficAttributions(modelId),
      (value) => `Sync complete: ${value?.subscribers_seen || 0} subscribers inspected`,
    )
    if (result) await load()
  }

  function beginEdit(segment) {
    setEditingId(segment.id)
    setDraft(segmentDraft(segment))
  }

  function beginCreate() {
    setEditingId('new')
    setDraft({ ...EMPTY_SEGMENT })
  }

  function change(field, value) {
    setDraft((current) => ({ ...current, [field]: value }))
  }

  async function saveSegment(event) {
    event.preventDefault()
    const payload = segmentPayload(draft)
    if (!payload.key || !payload.name) {
      setError('Segment key and name are required')
      return
    }
    const isNew = editingId === 'new'
    const result = await run(
      'save-segment',
      () => (isNew
        ? createTrafficSegment(modelId, payload)
        : updateTrafficSegment(modelId, editingId, payload)),
      isNew ? 'Traffic segment created' : 'Traffic segment updated',
    )
    if (result) {
      setEditingId(null)
      await load()
      if (sources.length) await discover()
    }
  }

  async function removeSegment(segment) {
    if (!window.confirm(`Delete traffic segment “${segment.name}”?`)) return
    const result = await run(
      `delete-segment-${segment.id}`,
      () => deleteTrafficSegment(modelId, segment.id).then(() => true),
      'Traffic segment deleted',
    )
    if (result) {
      if (editingId === segment.id) setEditingId(null)
      await load()
    }
  }

  async function mapSource(source) {
    const key = `${source.provider_link_id}:${source.tracking_link_id}`
    const segmentId = Number(sourceSegments[key] || 0)
    if (!segmentId) {
      setError('Choose a segment for this tracking link')
      return
    }
    const result = await run(
      `map-${key}`,
      () => upsertTrafficSourceRule(modelId, {
        provider_link_id: source.provider_link_id,
        segment_id: segmentId,
        tracking_link_id: source.tracking_link_id,
        campaign_code: source.campaign_code || null,
        campaign_name: source.campaign_name || null,
        priority: Number(sourcePriorities[key] || 0),
        is_active: true,
        source_metadata: source.metadata || {},
      }),
      `Mapped ${sourceTitle(source)}`,
    )
    if (result) {
      await load()
      await discover()
    }
  }

  async function unmapSource(source) {
    if (!source.rule_id) return
    const key = `${source.provider_link_id}:${source.tracking_link_id}`
    const result = await run(
      `unmap-${key}`,
      () => deleteTrafficSourceRule(modelId, source.rule_id).then(() => true),
      `Unmapped ${sourceTitle(source)}`,
    )
    if (result) {
      await load()
      await discover()
    }
  }

  if (loading) return <LoadingBlock label="Loading traffic-source policy..." />

  return (
    <Section
      className="v2-traffic-section"
      eyebrow="Acquisition context"
      title="Traffic-source segments"
      description="Map tracking links to a bounded persona overlay and sales pace. The existing graph, prices, safety rules and refusal handling remain authoritative."
      actions={(
        <div className="v2-card-actions">
          <button type="button" className="v2-btn v2-btn-ghost" disabled={Boolean(busy)} onClick={discover}>
            {busy === 'discover' ? 'Discovering...' : 'Discover links'}
          </button>
          <button type="button" className="v2-btn v2-btn-ghost" disabled={Boolean(busy) || !data?.enabled} onClick={syncNow}>
            {busy === 'sync' ? 'Syncing...' : 'Sync fans now'}
          </button>
          <button type="button" className={`v2-btn ${data?.enabled ? 'v2-btn-danger' : 'v2-btn-primary'}`} disabled={Boolean(busy) || !data?.segments?.length} onClick={toggleEnabled}>
            {busy === 'toggle' ? 'Saving...' : data?.enabled ? 'Disable' : 'Enable'}
          </button>
        </div>
      )}
    >
      <div className="v2-traffic-summary">
        <StatusPill tone={data?.enabled ? 'good' : 'neutral'}>{data?.enabled ? 'Enabled' : 'Default off'}</StatusPill>
        <span>{data?.segments?.length || 0} segments</span>
        <span>{data?.rules?.length || 0} mapped links</span>
        <span>{data?.attributed_fans || 0} attributed fans</span>
      </div>
      <p className="v2-traffic-note">
        First-touch attribution is sticky, so a fan does not suddenly change team voice mid-conversation. A manual fan override wins. If stacked links contain the same fan, the higher-priority rule wins on first assignment. Unknown traffic uses the active default segment.
      </p>
      {error ? <div className="v2-error">{error}</div> : null}
      {message ? <div className="v2-info">{message}</div> : null}

      {!data?.segments?.length ? (
        <EmptyState
          title="No traffic segments yet"
          description="Create safe presets, then customize them for each acquisition source. Nothing is enabled automatically."
          action={<button type="button" className="v2-btn v2-btn-primary" disabled={Boolean(busy)} onClick={bootstrap}>{busy === 'bootstrap' ? 'Creating...' : 'Create presets'}</button>}
        />
      ) : (
        <>
          <div className="v2-traffic-toolbar">
            <div>
              <strong>Segments</strong>
              <span>Use a neutral base persona; put source-specific team claims and sales pacing here.</span>
            </div>
            <button type="button" className="v2-btn v2-btn-primary" disabled={Boolean(busy)} onClick={beginCreate}>New segment</button>
          </div>
          <div className="v2-traffic-segment-grid">
            {data.segments.map((segment) => (
              <article key={segment.id} className={`v2-traffic-segment ${segment.is_active ? '' : 'is-muted'}`.trim()}>
                <div className="v2-traffic-segment-head">
                  <div>
                    <span>{segment.key}</span>
                    <h3>{segment.name}</h3>
                  </div>
                  <div className="v2-card-actions">
                    {segment.is_default ? <StatusPill tone="good">Default</StatusPill> : null}
                    <StatusPill tone={segment.is_active ? 'neutral' : 'warn'}>{segment.is_active ? segment.sales_policy?.intensity : 'Inactive'}</StatusPill>
                  </div>
                </div>
                <p>{segment.description || 'No description.'}</p>
                <dl>
                  <div><dt>First offer floor</dt><dd>Fan message {segment.sales_policy?.target_first_offer_turn}</dd></div>
                  <div><dt>Presell turns</dt><dd>{segment.sales_policy?.free_presell_turns}</dd></div>
                  <div><dt>Offer cooldown</dt><dd>{segment.sales_policy?.offer_cooldown_messages} messages</dd></div>
                  <div><dt>Fans</dt><dd>{segment.attribution_count || 0}</dd></div>
                </dl>
                {segment.persona_overlay?.label || segment.persona_overlay?.prompt ? (
                  <p className="v2-traffic-overlay-label">Overlay: {segment.persona_overlay.label || 'configured'}</p>
                ) : null}
                <div className="v2-card-actions">
                  <button type="button" className="v2-mini-button" onClick={() => beginEdit(segment)}>Edit</button>
                  <button type="button" className="v2-mini-button v2-mini-button-danger" disabled={Boolean(busy) || segment.is_default} onClick={() => removeSegment(segment)}>Delete</button>
                </div>
              </article>
            ))}
          </div>
        </>
      )}

      {editingId != null ? (
        <form className="v2-edit-form v2-traffic-editor" onSubmit={saveSegment}>
          <div className="v2-field-wide v2-traffic-editor-head">
            <div>
              <span>Segment editor</span>
              <h3>{editingId === 'new' ? 'New source strategy' : `Edit ${draft.name}`}</h3>
            </div>
            <button type="button" className="v2-mini-button" onClick={() => setEditingId(null)}>Close</button>
          </div>
          <label className="v2-field"><span>Stable key</span><input value={draft.key} onChange={(event) => change('key', event.target.value)} placeholder="cowboys_direct" /></label>
          <label className="v2-field"><span>Name</span><input value={draft.name} onChange={(event) => change('name', event.target.value)} placeholder="Cowboys direct traffic" /></label>
          <label className="v2-field v2-field-wide"><span>Description</span><input value={draft.description} onChange={(event) => change('description', event.target.value)} placeholder="What audience enters through this source?" /></label>
          <label className="v2-field"><span>Sales intensity</span><select value={draft.intensity} onChange={(event) => change('intensity', event.target.value)}>{INTENSITY_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
          <label className="v2-field"><span>Earliest unsolicited offer (fan message)</span><input type="number" min="1" max="50" value={draft.target_first_offer_turn} onChange={(event) => change('target_first_offer_turn', event.target.value)} /></label>
          <label className="v2-field"><span>Required presell turns</span><input type="number" min="0" max="12" value={draft.free_presell_turns} onChange={(event) => change('free_presell_turns', event.target.value)} /></label>
          <label className="v2-field"><span>Cooldown between offers</span><input type="number" min="0" max="50" value={draft.offer_cooldown_messages} onChange={(event) => change('offer_cooldown_messages', event.target.value)} /></label>
          <label className="v2-check-field"><input type="checkbox" checked={draft.proactive_presell} onChange={(event) => change('proactive_presell', event.target.checked)} /><span>Bot may initiate presell</span></label>
          <label className="v2-check-field"><input type="checkbox" checked={draft.relationship_first} onChange={(event) => change('relationship_first', event.target.checked)} /><span>Relationship-first pacing</span></label>
          <label className="v2-check-field"><input type="checkbox" checked={draft.is_active} onChange={(event) => change('is_active', event.target.checked)} /><span>Segment active</span></label>
          <label className="v2-check-field"><input type="checkbox" checked={draft.is_default} onChange={(event) => change('is_default', event.target.checked)} /><span>Default for unknown traffic</span></label>
          <label className="v2-field v2-field-wide"><span>Sales strategy notes</span><textarea value={draft.strategy_notes} onChange={(event) => change('strategy_notes', event.target.value)} placeholder="Lead toward an early relevant teaser; do not wait for the fan to ask." /></label>
          <label className="v2-field"><span>Overlay label</span><input value={draft.overlay_label} onChange={(event) => change('overlay_label', event.target.value)} placeholder="Cowboys acquisition voice" /></label>
          <label className="v2-field"><span>Approved facts (one per line)</span><textarea value={draft.overlay_facts} onChange={(event) => change('overlay_facts', event.target.value)} placeholder="She supports the Cowboys in this campaign context." /></label>
          <label className="v2-field v2-field-wide"><span>Persona/source overlay</span><textarea value={draft.overlay_prompt} onChange={(event) => change('overlay_prompt', event.target.value)} placeholder="For fans from this link, speak naturally as a committed Cowboys supporter. Do not mention another acquisition page." /></label>
          <label className="v2-field v2-field-wide"><span>Forbidden claims (one per line)</span><textarea value={draft.forbidden_claims} onChange={(event) => change('forbidden_claims', event.target.value)} placeholder="Do not claim live match details unless provided by an authoritative feed." /></label>
          <div className="v2-card-actions">
            <button type="submit" className="v2-btn v2-btn-primary" disabled={Boolean(busy)}>{busy === 'save-segment' ? 'Saving...' : 'Save segment'}</button>
            <button type="button" className="v2-btn v2-btn-ghost" disabled={Boolean(busy)} onClick={() => setEditingId(null)}>Cancel</button>
          </div>
        </form>
      ) : null}

      <div className="v2-traffic-toolbar">
        <div>
          <strong>Tracking-link mapping</strong>
          <span>Discovery currently uses the connected OnlyFansAPI account. New-subscription events trigger sync; a 15-minute recovery pass catches missed events.</span>
        </div>
        {!sources.length ? <button type="button" className="v2-btn v2-btn-ghost" disabled={Boolean(busy)} onClick={discover}>Discover tracking links</button> : null}
      </div>
      {sources.length ? (
        <div className="v2-table-card">
          <table className="v2-table">
            <thead><tr><th>Source</th><th>Audience</th><th>Segment</th><th>Priority</th><th>Action</th></tr></thead>
            <tbody>
              {sources.map((source) => {
                const key = `${source.provider_link_id}:${source.tracking_link_id}`
                const mapped = source.segment_id ? segmentsById[String(source.segment_id)] : null
                return (
                  <tr key={key}>
                    <td><strong>{sourceTitle(source)}</strong><br /><span>{source.campaign_code || source.tracking_link_id}</span></td>
                    <td>{source.subscribers_count || 0} subscribers<br /><span>{source.clicks_count || 0} clicks</span></td>
                    <td>
                      <select value={sourceSegments[key] || ''} onChange={(event) => setSourceSegments((current) => ({ ...current, [key]: event.target.value }))}>
                        <option value="">Choose segment</option>
                        {(data?.segments || []).filter((segment) => segment.is_active).map((segment) => <option key={segment.id} value={segment.id}>{segment.name}</option>)}
                      </select>
                      {mapped ? <small>Current: {mapped.name}</small> : null}
                    </td>
                    <td><input className="v2-traffic-priority" type="number" min="-1000" max="1000" value={sourcePriorities[key] ?? 0} onChange={(event) => setSourcePriorities((current) => ({ ...current, [key]: event.target.value }))} title="Higher wins when one fan appears in multiple tracking links" /></td>
                    <td><div className="v2-card-actions"><button type="button" className="v2-mini-button" disabled={Boolean(busy)} onClick={() => mapSource(source)}>Save mapping</button>{source.rule_id ? <button type="button" className="v2-mini-button v2-mini-button-danger" disabled={Boolean(busy)} onClick={() => unmapSource(source)}>Remove</button> : null}</div></td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      ) : data?.rules?.length ? (
        <div className="v2-table-card">
          <table className="v2-table">
            <thead><tr><th>Known mapping</th><th>Segment</th><th>Fans</th><th>Status</th></tr></thead>
            <tbody>{data.rules.map((rule) => <tr key={rule.id}><td>{rule.campaign_name || rule.campaign_code || rule.tracking_link_id}</td><td>{segmentsById[String(rule.segment_id)]?.name || `#${rule.segment_id}`}</td><td>{rule.attribution_count || 0}</td><td>{rule.is_active ? 'Active' : 'Inactive'}</td></tr>)}</tbody>
          </table>
        </div>
      ) : (
        <p className="v2-traffic-note">No tracking links are mapped yet. Unknown traffic continues to use the default segment only after this feature is enabled.</p>
      )}
    </Section>
  )
}
